import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'

import { TabPanel, TabPanels, Tabs } from '@wartek-id/tabs'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { Shimmer } from '@wartek-id/shimmer'
import { useToast } from '@wartek-id/toast'

import CustomTabList from '../../components/CustomTabList'
import { tabNamesV2 } from 'app/KinerjaPage/constants'
import { apiPaths, useAPIUpsertSKP } from 'api/kinerja/useAPIKinerja'
import DialogSaveDraft from 'app/KinerjaPage/components/DialogSaveDraft'
import PanelSummary from 'app/KinerjaPage/components/PanelSummary'
import TabPraktikPembelajaran from './TabPraktikPembelajaran'
import TabTugasTambahan from './TabTugasTambahan'
import TabPengembanganKompetensi from './TabPengembanganKompetensi'
import TabPerilakuKerja from './TabPerilakuKerja'
import TabRangkuman from './TabRangkuman'
import { translateSKPPeriod } from 'app/KinerjaPage/utils/kinerja'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'
import { useGetKinerjaQuerykeys } from 'api/kinerja/utils/api'

import type { Kinerja, SKPTabHandler, UpdateSKPPayload } from 'types/kinerja'

interface KinerjaPerencanaanAppProps {
  skp: Kinerja
  isSKPFetched: boolean
}

enum TabIndex {
  PRAKTIK_PEMBELAJARAN = 0,
  PENGEMBANGAN_KOMPENTESI = 1,
  TUGAS_TAMBAHAN = 2,
  PERILAKU_KERJA = 3,
  RANGKUMAN = 4,
}

function KinerjaPerencanaanApp({
  skp,
  isSKPFetched,
}: KinerjaPerencanaanAppProps) {
  const toast = useToast()
  const router = useRouter()
  const queryClient = useQueryClient()

  const [activeTabIndex, setActiveTabIndex] = useState(
    TabIndex.PRAKTIK_PEMBELAJARAN
  )

  const [isShowDialogSaveDraft, setIsShowDialogSaveDraft] = useState(false)

  const { mutateAsync: mutateSKP, isLoading: isUpdatingSKP } = useAPIUpsertSKP()
  const queryKeys = useGetKinerjaQuerykeys(apiPaths.kinerja())

  const isSKPApproved = skp?.status === 'APPROVED'

  useEffect(() => {
    if (skp?.status === 'NOT_FOUND') {
      router.push(`${PENGELOLAAN_KINERJA_GURU_PATH}/konfirmasi-profil`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skp])

  const goToHome = () => {
    router.push(PENGELOLAAN_KINERJA_GURU_PATH)
  }

  const handleClickBack = () => {
    if (skp?.status === 'DRAFT') {
      setIsShowDialogSaveDraft(true)
    } else {
      goToHome()
    }
  }

  const tabHandlerRef = useRef<SKPTabHandler>()

  const handleError = (error: any) => {
    console.error(error)

    const message =
      error?.originalError?.response?.data?.error?.message ?? error.toString()

    toast({
      message: `Gagal mengubah rencana hasil kerja: ${message}`,
    })
  }

  const updateSKP = async (data: UpdateSKPPayload) => {
    await mutateSKP(data)
    await queryClient.invalidateQueries({
      queryKey: queryKeys,
    })
  }

  const handleSaveDraft = async (data: UpdateSKPPayload) => {
    try {
      await updateSKP(data)
      goToHome()

      toast({
        message: 'Berhasil simpan draf!',
        icon: <Icon color="inverse">check</Icon>,
      })
    } catch (error) {
      handleError(error)
    }
  }

  const handleClickNext = async (data: UpdateSKPPayload, tabIndex: number) => {
    try {
      await updateSKP(data)

      setActiveTabIndex(tabIndex)
    } catch (error) {
      handleError(error)
    }
  }

  return (
    <div className="py-[50px] px-[116px]">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Icon onClick={handleClickBack} data-testid="icon-back">
            arrow_back
          </Icon>
          <Text variant="display-md">Perencanaan Kinerja</Text>
        </div>

        <PanelSummary
          isLoading={!isSKPFetched}
          items={[
            {
              label: translateSKPPeriod(skp.period),
              value: skp.tahun,
            },
          ]}
        />
      </div>

      {!isSKPFetched ? (
        <Shimmer data-testid="loading-spinner-skp" />
      ) : (
        <div className="bg-white rounded-md mt-8 mb-[78px]">
          <Tabs onChange={setActiveTabIndex} selectedIndex={activeTabIndex}>
            <CustomTabList
              activeTabIndex={activeTabIndex}
              tabs={!isSKPApproved ? tabNamesV2 : tabNamesV2.slice(0, 4)}
              disableInactiveTab
            />
            <TabPanels>
              <TabPanel>
                <TabPraktikPembelajaran
                  ref={tabHandlerRef}
                  skp={skp}
                  onClickSave={handleSaveDraft}
                  isSubmitting={isUpdatingSKP}
                  onClickNext={(data) =>
                    handleClickNext(data, TabIndex.PENGEMBANGAN_KOMPENTESI)
                  }
                />
              </TabPanel>
              <TabPanel>
                <TabPengembanganKompetensi
                  ref={tabHandlerRef}
                  skp={skp}
                  onClickSave={handleSaveDraft}
                  onClickNext={(data) =>
                    handleClickNext(data, TabIndex.TUGAS_TAMBAHAN)
                  }
                  isSubmitting={isUpdatingSKP}
                />
              </TabPanel>
              <TabPanel>
                <TabTugasTambahan
                  ref={tabHandlerRef}
                  skp={skp}
                  onClickSave={handleSaveDraft}
                  onClickNext={(data) =>
                    handleClickNext(data, TabIndex.PERILAKU_KERJA)
                  }
                  isSubmitting={isUpdatingSKP}
                />
              </TabPanel>
              <TabPanel>
                <TabPerilakuKerja
                  ref={tabHandlerRef}
                  skp={skp}
                  onClickSave={handleSaveDraft}
                  isLoading={isUpdatingSKP}
                  onClickNext={(data) =>
                    handleClickNext(data, TabIndex.RANGKUMAN)
                  }
                />
              </TabPanel>
              <TabPanel>
                <TabRangkuman
                  ref={tabHandlerRef}
                  skp={skp}
                  isSubmitting={isUpdatingSKP}
                  onRequestChange={() =>
                    setActiveTabIndex(TabIndex.PRAKTIK_PEMBELAJARAN)
                  }
                  onClickBackToHome={goToHome}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </div>
      )}

      <DialogSaveDraft
        data-testid="dialog-save-draft"
        isOpen={isShowDialogSaveDraft}
        isLoading={isUpdatingSKP}
        onClose={() => setIsShowDialogSaveDraft(false)}
        onClickCancel={() => setIsShowDialogSaveDraft(false)}
        onClickConfirm={() => {
          tabHandlerRef.current?.saveDraft()
        }}
      />
    </div>
  )
}

export default KinerjaPerencanaanApp
